import PropTypes from 'prop-types';
import { Box, MenuItem, Select as MuiSelect, Typography } from '@mui/material';

import { getStyle } from 'helper/getStyle';
import { menuStyles, MENU_SHADOW } from 'components/simple/MenuPanel';
import { ButtonArrowDownIcon } from 'assets/icons';

const styles = {
  select: ({ palette }) => ({
    height: 50,
    backgroundColor: palette.background.paper,
    borderRadius: 2.5,
    flex: '1 0',
    '.MuiSelect-select': {
      paddingBlock: 1.5,
      '&.Mui-readOnly': {
        cursor: 'default',
      },
    },
    '.MuiOutlinedInput-notchedOutline': {
      border: `1px solid ${palette.colors.darkPale} !important`,
    },
  }),
  menu: ({ palette }) => ({
    borderRadius: 2.5,
    boxShadow: MENU_SHADOW,
    maxHeight: 350,
    marginTop: 0.5,
    '.MuiMenu-list': {
      padding: 'unset !important',
      border: `1px solid ${palette.colors.darkPale}`,
    },
  }),
  menuItem: (theme) => ({
    ...menuStyles.selectOption(theme),
    paddingBlock: 1.5,
    paddingInline: 2,
  }),
  selectIcon: ({ palette }) => ({
    color: palette.primary.main,
    width: 12,
    height: 12,
    position: 'absolute',
    right: 16,
    top: 'calc(50% - 6px)',
    cursor: 'pointer',
  }),
  readOnlySelectIcon: {
    opacity: 0,
    cursor: 'default',
  },
};

export const Select = ({
  onChange = () => {},
  options,
  customStyles = {},
  readOnly = false,
  innerRef = null,
  disabled = false,
  ...props
}) => {
  return (
    <MuiSelect
      {...props}
      ref={innerRef}
      variant="outlined"
      readOnly={readOnly}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
      IconComponent={() => (
        <Box
          component={ButtonArrowDownIcon}
          sx={[
            (theme) => getStyle(styles.selectIcon, theme),
            (theme) => readOnly && getStyle(styles.readOnlySelectIcon, theme),
            (theme) => getStyle(customStyles.selectIcon, theme),
          ]}
        />
      )}
      MenuProps={{
        disableAutoFocusItem: true,
        disablePortal: true,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: 'left',
        },
        slotProps: {
          paper: { sx: [(theme) => getStyle(styles.menu, theme), (theme) => getStyle(customStyles.menu, theme)] },
        },
      }}
      sx={[(theme) => getStyle(styles.select, theme), (theme) => getStyle(customStyles.select, theme)]}
    >
      {options.map((option) => (
        <MenuItem
          divider
          disableRipple
          disableGutters
          key={option.value}
          value={option.value}
          sx={[(theme) => getStyle(styles.menuItem, theme), (theme) => getStyle(customStyles.menuItem, theme, option)]}
        >
          <Typography variant="body1" color="inherit" noWrap mt={0.25}>
            {option.label}
          </Typography>
        </MenuItem>
      ))}
    </MuiSelect>
  );
};

const valuePropTypes = PropTypes.oneOfType([PropTypes.string, PropTypes.number]);

Select.propTypes = {
  value: valuePropTypes.isRequired,
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: valuePropTypes,
    }).isRequired,
  ),
  customStyles: PropTypes.shape({
    select: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
    selectIcon: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
    menu: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
    menuItem: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  }),
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({})]),
};
